import React, { useState } from 'react';
import { Location } from '../../types/route';
import { useLocations } from '../../hooks/useLocations';
import { FaSearch, FaMapMarkerAlt, FaSpinner, FaTimes, FaExclamationCircle } from 'react-icons/fa';
import { UseFormRegister, FieldError, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { RouteFormData } from '../../types/route';

interface LocationSelectorProps {
  register: UseFormRegister<RouteFormData>;
  setValue: UseFormSetValue<RouteFormData>;
  watch: UseFormWatch<RouteFormData>;
  error?: FieldError;
  label: string;
  fieldPath: 'source.data' | 'target.data';
}

const LocationSelector: React.FC<LocationSelectorProps> = ({ 
  register, 
  setValue, 
  watch, 
  error, 
  label, 
  fieldPath 
}) => {
  const { locations, loading, searchLocations } = useLocations();
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const currentValue = watch(fieldPath) as Location;

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    if (query.length > 2) {
      setHasSearched(true);
      searchLocations(query);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
      setHasSearched(false);
    }
  };

  const handleSelect = (location: Location) => {
    setValue(`${fieldPath}.criteria0`, location.criteria0 || '', { shouldValidate: true });
    setValue(`${fieldPath}.criteria1`, location.criteria1 || '', { shouldValidate: true });
    setValue(`${fieldPath}.criteria2`, location.criteria2 || '', { shouldValidate: true });
    setValue(`${fieldPath}.criteria3`, location.criteria3 || '', { shouldValidate: true });
    setShowDropdown(false);
    setSearchQuery('');
    setHasSearched(false);
  };

  const handleClear = () => {
    setValue(`${fieldPath}.criteria0`, '', { shouldValidate: true });
    setValue(`${fieldPath}.criteria1`, '', { shouldValidate: true });
    setValue(`${fieldPath}.criteria2`, '', { shouldValidate: true });
    setValue(`${fieldPath}.criteria3`, '', { shouldValidate: true });
  };

  const hasSelection = currentValue?.criteria0 || currentValue?.criteria1 || currentValue?.criteria2 || currentValue?.criteria3;
  const showNoResults = hasSearched && !loading && locations.length === 0 && searchQuery.length > 2;

  const getLocationDisplay = () => {
    return [currentValue?.criteria0, currentValue?.criteria1, currentValue?.criteria2, currentValue?.criteria3]
      .filter(Boolean)
      .join(' › ');
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Buscar {label}
        </label>
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={`Buscar ${label.toLowerCase()}...`}
            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              error ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
            }`}
          />
          {loading && (
            <FaSpinner className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 animate-spin" />
          )}
        </div>
        
        {showDropdown && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {locations.length > 0 ? (
              locations.map((location, index) => (
                <button
                  key={location.id || index}
                  type="button"
                  onClick={() => handleSelect(location)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3 border-b border-gray-100 last:border-b-0"
                >
                  <FaMapMarkerAlt className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {[location.criteria0, location.criteria1, location.criteria2, location.criteria3]
                        .filter(Boolean).join(' › ')}
                    </div>
                  </div>
                </button>
              ))
            ) : showNoResults ? (
              <div className="px-4 py-3 text-center text-gray-500">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <FaExclamationCircle className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium">No se encontraron resultados</span>
                </div>
                <p className="text-xs text-gray-400">
                  Ninguna ubicación coincide con "{searchQuery}". Intente con otro término de búsqueda.
                </p>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Hidden form fields for react-hook-form */}
      <div className="hidden">
        <input
          {...register(`${fieldPath}.criteria0`)}
          type="hidden"
        />
        <input
          {...register(`${fieldPath}.criteria1`)}
          type="hidden"
        />
        <input
          {...register(`${fieldPath}.criteria2`)}
          type="hidden"
        />
        <input
          {...register(`${fieldPath}.criteria3`)}
          type="hidden"
        />
      </div>

      {/* Display current selection - Read-only */}
      {hasSelection && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <FaMapMarkerAlt className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-800">Ubicación seleccionada</span>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors"
              title="Borrar selección"
            >
              <FaTimes className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-3">
            <div className="text-sm font-medium text-green-900 bg-green-100 px-3 py-2 rounded border">
              {getLocationDisplay() || 'Sin jerarquía de ubicación'}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-green-700 mb-1">Criterio 0</label>
                <div className="text-sm text-green-900 bg-green-100 px-3 py-2 rounded border">
                  {currentValue?.criteria0 || 'No especificado'}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-green-700 mb-1">Criterio 1</label>
                <div className="text-sm text-green-900 bg-green-100 px-3 py-2 rounded border">
                  {currentValue?.criteria1 || 'No especificado'}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-green-700 mb-1">Criterio 2</label>
                <div className="text-sm text-green-900 bg-green-100 px-3 py-2 rounded border">
                  {currentValue?.criteria2 || 'No especificado'}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-green-700 mb-1">Criterio 3</label>
                <div className="text-sm text-green-900 bg-green-100 px-3 py-2 rounded border">
                  {currentValue?.criteria3 || 'No especificado'}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Validation error message */}
      {error && (
        <div className="text-sm text-red-600 flex items-center space-x-1">
          <FaExclamationCircle className="w-3 h-3" />
          <span>{error.message}</span>
        </div>
      )}

      {/* Validation message */}
      {!hasSelection && !error && (
        <div className="text-sm text-gray-500 italic">
          Por favor, busque y seleccione una ubicación del menú desplegable de arriba
        </div>
      )}
    </div>
  );
};

export default LocationSelector;